import React, { useCallback, useEffect, useState } from "react";
import { Platform, ProductData } from "../models";
import { getProductCollection, getVariant } from "../services/conditions.service";
import { ConditionFact } from "@reconvert/reconvert-utils";
import { APP_PLATFORM } from "../config";

interface ProductSelectorProps {
  conditionFact: string;
  value: {
    productIds: string[];
    variantIds: string[];
    wholeProductIds?: string[];
    productCollectionIds?: { id: string; name: string }[];
  };
  setValue: (val: {
    productIds: string[];
    variantIds: string[];
    wholeProductIds?: string[];
    productCollectionIds?: { id: string; name: string }[];
    itemsInfo?: string[];
  }) => void;
}

interface ProductSelectorReturnType {
  selectedProductIdObj: Record<string, string[]>;
  isProductsLoading: boolean;
  selectedProducts: ProductData[];
  selectedProductCollections?: { id: string; name: string }[];
  handleRemoveProduct: (productId: string) => void;
  updateSelectedProductIdObj: (value: Record<string, string[]>, wholeProductHints?: Record<string, boolean>) => void;
}

// Maps of picked products are keyed by product id. `value` uses the condition's own field names
// as keys, so those must never be read back as product ids by the pickers or the helpers below.
const VALUE_FIELD_KEYS = new Set(["productIds", "variantIds", "wholeProductIds", "productCollectionIds"]);

export const getPickedProductIds = (productIdObj: Record<string, string[]>) =>
  Object.keys(productIdObj ?? {}).filter(key => !VALUE_FIELD_KEYS.has(key));

const areSameIdLists = (left?: string[], right?: string[]) => {
  if ((left?.length ?? 0) !== (right?.length ?? 0)) {
    return false;
  }

  const rightIds = new Set(right ?? []);

  return (left ?? []).every(id => rightIds.has(id));
};

const useProductSelector = ({ value, setValue, conditionFact }: ProductSelectorProps): ProductSelectorReturnType => {
  // Starts empty: the picked products are only known once their variants have been loaded below.
  const [selectedProductIdObj, setSelectedProductIdObj] = useState<Record<string, string[]>>({});

  const [selectedProducts, setSelectedProducts] = useState<ProductData[]>([]);
  const [selectedProductCollections, setSelectedProductCollections] = useState<{ id: string; name: string }[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(false);

  useEffect(() => {
    setSelectedProducts([]);
    setSelectedProductIdObj({});
    setSelectedProductCollections([]);
  }, [conditionFact]);

  // A product targets its whole self when the merchant never narrowed it down to specific
  // variants. Its variants are then resolved at evaluation time, so variants added to the
  // product later still match.
  //
  // Pickers already know which products were taken as a whole and say so through
  // `wholeProductHints`. Loaded product data is only a fallback, because a product picked
  // moments ago has none yet.
  const getWholeProductIds = useCallback(
    (
      productIdObj: Record<string, string[]>,
      products: ProductData[],
      wholeProductHints?: Record<string, boolean>,
    ) => {
      return getPickedProductIds(productIdObj)
        .filter(productId => {
          const selectedVariantCount = productIdObj[productId]?.length ?? 0;

          // Pickers opened without variant selection return no variants at all.
          if (selectedVariantCount === 0) {
            return true;
          }

          const hint = wholeProductHints?.[productId];

          if (hint !== undefined) {
            return hint;
          }

          const totalVariants = products.find(p => p.id === productId)?.variants?.length ?? 0;

          // Until the product's variants are loaded the selection is treated as variant specific,
          // which keeps an unresolved state from widening what the condition matches.
          return totalVariants > 0 && selectedVariantCount >= totalVariants;
        });
    },
    [],
  );

  useEffect(() => {
    if (!selectedProducts.length) {
      return;
    }

    const nextWholeProductIds = getWholeProductIds(selectedProductIdObj, selectedProducts);
    const hasStoredWholeProductIds = value.wholeProductIds != null;
    // Conditions saved before this field existed omit it and still mean "every product is
    // whole". Filling the list in on load looks like an edit and shows the unsaved bar.
    const shouldWriteWholeProductIds =
      hasStoredWholeProductIds && !areSameIdLists(value.wholeProductIds, nextWholeProductIds);

    setValue({
      ...value,
      itemsInfo: selectedProducts.map(p => p.title),
      ...(shouldWriteWholeProductIds ? { wholeProductIds: nextWholeProductIds } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts]);

  useEffect(() => {
    if (!value.productIds?.length) {
      setSelectedProducts([]);
    } else {
      const hasNewProduct = value.productIds.some(id => !selectedProducts.find(p => p.id === id));

      if (hasNewProduct) {
        getSelectedProducts();
      } else {
        setSelectedProducts(selectedProducts.filter(p => value.productIds.includes(p.id)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.productIds]);

  const getSelectedProductCollections = useCallback(async () => {
    const collectionsIds = value?.productCollectionIds?.map(a => a.id);

    const collectionsResponse = await getProductCollection({
      limit: APP_PLATFORM === Platform.Wix ? 100 : 250,
      collectionIds: collectionsIds?.length ? collectionsIds.join(",") : undefined,
    });

    setSelectedProductCollections(
      collectionsResponse?.collections.map((item: any) => {
        return {
          id: item.id.split("/").pop(),
          name: item.title,
        };
      }),
    );
  }, [value?.productCollectionIds]);

  useEffect(() => {
    (async () => {
      if (conditionFact === "PRODUCT_COLLECTION") {
        getSelectedProductCollections();
      }
    })();
  }, [conditionFact, getSelectedProductCollections, value.productCollectionIds]);

  const getSelectedProducts = useCallback(async () => {
    setIsProductsLoading(true);

    const regex = /^(0|-)+$/;
    let products: ProductData[] = [];

    let newSelectedProductIdObj: any = {};

    switch (conditionFact) {
      case "SPECIFIC_PRODUCT":
        const response = await getVariant(value.productIds);

        if (response) {
          products = response?.map((item: any) => {
            return {
              ...item,
              id: item?.id.split("/").pop(),
              variants: item?.variants?.map((variant: any) => {
                return {
                  ...variant,
                  id: regex.test(variant?.id?.split("/")?.pop())
                    ? item?.id.split("/").pop()
                    : variant?.id?.split("/")?.pop(),
                };
              }),
            };
          });

          value.productIds?.forEach((productId: any) => {
            products!.forEach((product: any) => {
              if (product.id === productId) {
                // Conditions saved before variant selection existed carry no marker and matched
                // the whole product, so a missing marker re-selects every variant.
                const isWholeProduct = !value.wholeProductIds || value.wholeProductIds.includes(productId);

                newSelectedProductIdObj[productId] = isWholeProduct
                  ? product.variants.map((variant: any) => variant.id)
                  : product.variants
                      .filter((variant: any) => value.variantIds.includes(variant?.id))
                      .map((variant: any) => variant.id);
              }
            });
          });
        }

        setSelectedProductIdObj(newSelectedProductIdObj);

        setSelectedProducts(products);

        break;

      case "PRODUCT_COLLECTION":
        getSelectedProductCollections();

        break;
    }

    setIsProductsLoading(false);
  }, [value]);

  const updateSelectedProductIdObj = (
    value: Record<string, string[]>,
    wholeProductHints?: Record<string, boolean>,
  ) => {
    setSelectedProductIdObj(value);
    const productIds = getPickedProductIds(value);
    const variantIds = productIds.flatMap(productId => value[productId] ?? []);

    if (conditionFact === ConditionFact.SPECIFIC_PRODUCT) {
      setValue({
        productIds,
        variantIds,
        wholeProductIds: getWholeProductIds(value, selectedProducts, wholeProductHints),
      });
    }

    if (conditionFact === ConditionFact.PRODUCT_COLLECTION) {
      const data = Object.entries(value).map(([key, value]) => ({
        id: key,
        name: value[0],
      }));

      setValue({ productCollectionIds: data, productIds: [], variantIds: [], itemsInfo: data?.map(a => a.name) });
    }
  };

  const handleRemoveProduct = (productId: string) => {
    switch (conditionFact) {
      case ConditionFact.SPECIFIC_PRODUCT:
        const newValue = { ...selectedProductIdObj };

        delete newValue[productId];
        updateSelectedProductIdObj(newValue);
        break;

      case ConditionFact.PRODUCT_COLLECTION:
        setSelectedProductCollections(prev => prev.filter(a => a.id !== productId));
        updateSelectedProductIdObj(
          selectedProductCollections
            .filter(a => a.id !== productId)
            ?.reduce((acc, item) => {
              acc[item.id] = acc[item.id] || [];
              acc[item.id].push(item.name);
              return acc;
            }, {} as any),
        );
        break;

      default:
        break;
    }
  };

  return {
    selectedProductIdObj,
    isProductsLoading,
    selectedProducts,
    selectedProductCollections,
    handleRemoveProduct,
    updateSelectedProductIdObj,
  };
};

export default useProductSelector;
